/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useAuth } from "../../../../context/AuthContext";
import { formatCurrency } from "../../../../utils/format";
import { useSelector } from "react-redux";
import ControlledField from "./ControlledField";
import AddressFields from "./AddressFields";
import DisclaimerModal from "../../popups/DisclaimerModal";
import EmailModal from "../../popups/EmailModal";
import { useModal } from "../../../../context/ModalContext";
import { CommonHeader } from "../../../../components/ui/CommonHeader";
import { buildBuyerPayload } from "../../../../utils/payloadBuilder";

export default function ManualSheetSection(props) {
  const {
    formData,
    setFormData,
    FloatingInput,
    FloatingNumber,
    handlePropNineEstimate,
    checkValidSalesPrice,
    handleCurrencyBlur,
    isInitialLoad,
    showToaster,
    handleDownloadPrintPDF,
    useAnnualPropertyTax,
  } = props;

  const { user } = useAuth();
  const ucWords = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  const fullName = ucWords(
    `${user.first_name ?? ""} ${user.last_name ?? ""}`
  ).trim();

  const buyerformData = useSelector((state) => state.buyer.formData);
  // const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("settings365");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultValues = useMemo(
    () =>
      ({
        ...formData,
      }[formData])
  );

  const { control, setValue } = useForm({ defaultValues });

  const { openModal, closeModal } = useModal();

  useEffect(() => {
    if (user?.first_name || user?.last_name) {
      setFormData((prev) => ({
        ...prev,
        preparedBy: fullName,
      }));

      // Also update RHF value
      setValue("preparedBy", fullName);
    }
  }, [user, setFormData, setValue, fullName]);

  const cleanNumber = (v) => {
    if (v === undefined || v === null) return "";
    const s = typeof v === "string" ? v : String(v);
    return s.replace(/[^0-9.]/g, "");
  };

  // keep original UI structure and classes exactly; replace inputs with Controller wrappers
  return (
    <>
      <div className="model-body-calc-pg">
        <div className="calculator-page application-fe">
          <CommonHeader
            title={`Prop 19 Calculator for Primary Residence`}
          ></CommonHeader>

          <div className="row-three-wrap">
            <div className="calc-column-a">
              <h3 className="m-btm-16">
                General information{" "}
                <span className="heading-e">Recommended input</span>
              </h3>

              <div className="calc-frm-field full-dvd">
                <div className="calc-frm-fld form-group input-sticky">
                  <Controller
                    name="preparedBy"
                    control={control}
                    defaultValue={formData?.preparedBy ?? ""}
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        placeholder=""
                        label="Prepared by"
                        type="text"
                        onBlur={(e) => {
                          field.onChange(e);
                          setFormData((p) => ({
                            ...p,
                            preparedBy: e.target.value,
                          }));
                        }}
                      />
                    )}
                  />
                </div>
                <div className="calc-frm-fld form-group input-sticky">
                  <Controller
                    name="preparedFor"
                    control={control}
                    defaultValue={formData?.preparedFor ?? ""}
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        placeholder="New Client"
                        label="Prepared for"
                        type="text"
                        onBlur={(e) =>
                          setFormData((p) => ({
                            ...p,
                            preparedFor: e.target.value,
                          }))
                        }
                      />
                    )}
                  />
                </div>
              </div>

              <AddressFields
                mode="propnine2"
                control={control}
                FloatingInput={FloatingInput}
                formData={formData}
                openModal={openModal}
                closeModal={closeModal}
                setValue={setValue}
                setFormData={setFormData}
                isInitialLoad={isInitialLoad}
                showToaster={showToaster}
                // setCompanyId={props.setCompanyId}
              />

              <h3 className="m-top-24 m-btm-16 prop-field-heading">
                Home Price & Tax Rate (Required)
              </h3>
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="assessedValueAmount"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Current Home - Tax Assessed Value"
                    type="text"
                    className="input-filled"
                    defaultValue={
                      formData.loanInfoMeta?.assessedValueAmount || ""
                    }
                    cleanNumber={cleanNumber}
                    disabled={useAnnualPropertyTax}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) => {
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          loanInfoMeta: {
                            ...(prev.loanInfoMeta || {}),
                            assessedValueAmount: formatted,
                          },
                        };

                        handlePropNineEstimate(updated); // ✅ PASS IT
                        return updated;
                      });
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="salesPrice"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Current Home - Sales Price"
                    placeholder="0.00"
                    type="text"
                    className="input-filled"
                    defaultValue={formData.salesPrice || ""}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) => {
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          salesPrice: formatted,
                        };

                        handlePropNineEstimate(updated); // ✅ PASS IT
                        return updated;
                      });
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="purchasePrice"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="New Home - Purchase Price"
                    placeholder="0.00"
                    type="text"
                    className="input-filled"
                    defaultValue={formData.purchasePrice || ""}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) => {
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          purchasePrice: formatted,
                        };

                        handlePropNineEstimate(updated); // ✅ PASS IT
                        return updated;
                      });
                    }}
                  />
                </div>
              </div>

              <div className="">
                <div className="calc-frm-fld form-group sign-of-percentage">
                  <ControlledField
                    name="caTaxRate"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Property Tax Rate"
                    placeholder="0.00"
                    type="text"
                    className="input-filled"
                    defaultValue={formData.loanInfoMeta?.caTaxRate || ""}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) => {
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          loanInfoMeta: {
                            ...prev.loanInfoMeta,
                            caTaxRate: formatted,
                          },
                        };

                        handlePropNineEstimate(updated); // ✅ PASS IT
                        return updated;
                      });
                    }}
                  />
                </div>
                <div className="calc-frm-fld form-group"></div>
              </div>
            </div>

            <div className="calc-column-d">
              <h3 className="m-btm-16 prop-heading">Estimated Taxes</h3>

              {/* ✅ Payment Section */}
              <div className="box-wrap layout-box">
                <div className="box-layout-head">
                  {/* Tab Navigation */}
                  <div className="settings-tabs-wrapper">
                    <ul className="settings-tabs">
                      <li
                        className={`tab-item ${
                          activeTab === "settings365" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("settings365")}
                      >
                        0-365 Days Between Sales
                      </li>
                      <li
                        className={`tab-item ${
                          activeTab === "settings730" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("settings730")}
                      >
                        366-730 Days Between Sales
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Tab Content */}
                <div className="tab-content fade-in mt-8 profile-mysetting">
                  {activeTab === "settings365" && (
                    <div className="propnine-list">
                      <div className="property-question">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Current Home Adjusted Full Cash Value
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween365
                                ?.adjustedCash
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Difference in Sale / Purchase Price
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween365
                                ?.diffSaleAndPurchase
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Taxable Value of New Home
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween365
                                ?.taxableValue
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question hightlighted-prop">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Taxes with Prop 19 (${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween365
                                ?.taxableValue
                            )}{" "}
                            x{" "}
                            {formatCurrency(
                              buyerformData?.loanInfoMeta?.caTaxRate
                            )}{" "}
                            %)
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group ">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween365
                                ?.taxWithProp19
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question hightlighted-prop">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Taxes without Prop 19 (${" "}
                            {formatCurrency(buyerformData?.purchasePrice)} x{" "}
                            {formatCurrency(
                              buyerformData?.loanInfoMeta?.caTaxRate
                            )}{" "}
                            %)
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween365
                                ?.taxWithoutProp19
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "settings730" && (
                    <div className="propnine-list">
                      <div className="property-question">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Current Home Adjusted Full Cash Value
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween730
                                ?.adjustedCash
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Difference in Sale / Purchase Price
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween730
                                ?.diffSaleAndPurchase
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Taxable Value of New Home
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween730
                                ?.taxableValue
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question hightlighted-prop">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Taxes with Prop 19 (${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween730
                                ?.taxableValue
                            )}{" "}
                            x{" "}
                            {formatCurrency(
                              buyerformData?.loanInfoMeta?.caTaxRate
                            )}{" "}
                            %)
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group ">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween730
                                ?.taxWithProp19
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="property-question hightlighted-prop">
                        <div className="content-description">
                          <p className="heading-b txtclr">
                            Taxes without Prop 19 (${" "}
                            {formatCurrency(buyerformData?.purchasePrice)} x{" "}
                            {formatCurrency(
                              buyerformData?.loanInfoMeta?.caTaxRate
                            )}{" "}
                            %)
                          </p>
                        </div>
                        <div className="form-value">
                          <div className="form-group">
                            ${" "}
                            {formatCurrency(
                              buyerformData?.estimatedTaxes?.saleBetween730
                                ?.taxWithoutProp19
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ✅ Footer Buttons */}
                <div className="box-action box-propline">
                  <ul>
                    <li>
                      <button
                        className="icon-pdf"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          handleDownloadPrintPDF("download", buyerformData);
                        }}
                      >
                        Save PDF
                      </button>
                    </li>
                    <li>
                      <button
                        className="icon-print"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          handleDownloadPrintPDF("print", buyerformData);
                        }}
                      >
                        Print
                      </button>
                    </li>
                    <li>
                      <button
                        className="icon-email"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          e.preventDefault();
                          //   console.log(formData);
                          const updated = buildBuyerPayload({
                            buyerformData,
                            user,
                          });
                          openModal({
                            title: "Email a Closing Cost Estimate",
                            size: "mediumsizewidth",
                            content: (
                              <EmailModal
                                onClose={closeModal}
                                type="buyer"
                                showToaster={showToaster}
                                buyerFormData={updated}
                              />
                            ),
                          });
                        }}
                      >
                        Email
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* ✅ Disclaimer */}
              <div className="calc-frm-field">
                <a
                  href=""
                  className="icon-info"
                  onClick={(e) => {
                    e.preventDefault();
                    openModal({
                      title: "Disclaimer",
                      size: "mediumsizewidth",
                      content: (
                        <DisclaimerModal calc="buyer" onClose={closeModal} />
                      ),
                    });
                  }}
                >
                  Disclaimer
                </a>
              </div>
            </div>

            <div class="about-prop-section">
              <h4>About Prop 19</h4>
              <p>
                California Proposition 19 is a Constitutional Amendment that
                includes two major property tax benefit changes:
              </p>
              <p>
                1. Additional limits are imposed on property tax benefits for
                inherited family property. A child (or children) beneficiary may
                only keep the lower property tax base of the parent(s) if the
                property is the principal residence of the parent(s) and the
                child (or children) make it their primary place of residence
                within one year.
              </p>
              <p>
                2. Homeowners over 55 years of age, disabled, or victims of a
                wildfire or natural disaster may transfer the base year property
                value of their primary home to a newly purchased or newly
                constructed replacement primary residence up to three times (or
                once per disaster). These homeowners may transfer this tax base
                to a property located anywhere in the state of California within
                two years of the sale of the original residence. For more
                information, please visit the California State Board of
                Equalization (BOE) website for updates, as additional
                legislation verbiage can provide further clarification.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
